import { hexToRgb, mix, tint } from './colorMath';
import type { AppTheme, UiConfig } from './theme';

/**
 * Tmavý režim = přepis barev stávajících Tailwind tříd (bg-white, text-gray-900, bg-green-50 …).
 * Aplikace je psaná se světlými třídami, proto se jejich barvy mění centrálně tady a nic se nemusí
 * přepisovat po stránkách. Celé je to v @media screen – tisk zůstává vždy světlý.
 */

export const DARK = {
  page: '#0d1117',     // pozadí aplikace
  card: '#161b22',     // karty, okna, horní lišta (bg-white)
  raised: '#1c232d',   // bg-gray-50, hover
  input: '#0f141b',    // políčka formulářů
};

const rgba = (hex: string, a: number) => { const [r, g, b] = hexToRgb(hex); return `rgba(${r},${g},${b},${a})`; };
const esc = (c: string) => c.replace(/[:/]/g, m => '\\' + m);

type Map = Record<string, string>;

const GRAY_BG: Map = { white: DARK.card, '50': DARK.raised, '100': '#232b37', '200': '#2d3644', '300': '#3a4556', '400': '#4b5668', '700': '#475569', '800': '#2b3544', '900': '#334155' };
const GRAY_TEXT: Map = { '200': '#4a5465', '300': '#5d6879', '400': '#778295', '500': '#8d99ab', '600': '#b3bcc9', '700': '#d3d9e2', '800': '#e6eaf0', '900': '#f1f5f9' };
const GRAY_BORDER: Map = { '50': '#1a212b', '100': '#1f2731', '200': '#2a3340', '300': '#3a4556', '400': '#4b5668', '700': '#3a4556', '900': '#9aa5b5' };

/** Tailwind paleta: [text 300, text 400, základ 500] */
const FAM: Record<string, [string, string, string]> = {
  red: ['#fca5a5', '#f87171', '#ef4444'], green: ['#86efac', '#4ade80', '#22c55e'], amber: ['#fcd34d', '#fbbf24', '#f59e0b'],
  orange: ['#fdba74', '#fb923c', '#f97316'], yellow: ['#fde047', '#facc15', '#eab308'], teal: ['#5eead4', '#2dd4bf', '#14b8a6'],
  emerald: ['#6ee7b7', '#34d399', '#10b981'], violet: ['#c4b5fd', '#a78bfa', '#8b5cf6'], purple: ['#d8b4fe', '#c084fc', '#a855f7'],
  indigo: ['#a5b4fc', '#818cf8', '#6366f1'], sky: ['#7dd3fc', '#38bdf8', '#0ea5e9'], cyan: ['#67e8f9', '#22d3ee', '#06b6d4'],
  pink: ['#f9a8d4', '#f472b6', '#ec4899'], rose: ['#fda4af', '#fb7185', '#f43f5e'],
};

/** Původní (světlé) barvy pro oblasti, které mají zůstat jako papír – třída .keep-light. */
const L_FAM: Record<string, { t6: string; t7: string; b50: string; b100: string; bd200: string }> = {
  red:     { t6: '#dc2626', t7: '#b91c1c', b50: '#fef2f2', b100: '#fee2e2', bd200: '#fecaca' },
  green:   { t6: '#16a34a', t7: '#15803d', b50: '#f0fdf4', b100: '#dcfce7', bd200: '#bbf7d0' },
  amber:   { t6: '#d97706', t7: '#b45309', b50: '#fffbeb', b100: '#fef3c7', bd200: '#fde68a' },
  orange:  { t6: '#ea580c', t7: '#c2410c', b50: '#fff7ed', b100: '#ffedd5', bd200: '#fed7aa' },
  teal:    { t6: '#0d9488', t7: '#0f766e', b50: '#f0fdfa', b100: '#ccfbf1', bd200: '#99f6e4' },
  emerald: { t6: '#059669', t7: '#047857', b50: '#ecfdf5', b100: '#d1fae5', bd200: '#a7f3d0' },
  violet:  { t6: '#7c3aed', t7: '#6d28d9', b50: '#f5f3ff', b100: '#ede9fe', bd200: '#ddd6fe' },
  purple:  { t6: '#9333ea', t7: '#7e22ce', b50: '#faf5ff', b100: '#f3e8ff', bd200: '#e9d5ff' },
};
const L_GRAY_TEXT: Map = { '200': '#e5e7eb', '300': '#d1d5db', '400': '#9ca3af', '500': '#6b7280', '600': '#4b5563', '700': '#374151', '800': '#1f2937', '900': '#111827' };
const L_GRAY_BG: Map = { '50': '#f9fafb', '100': '#f3f4f6', '200': '#e5e7eb', '300': '#d1d5db' };
const L_GRAY_BORDER: Map = { '100': '#f3f4f6', '200': '#e5e7eb', '300': '#d1d5db', '400': '#9ca3af', '900': '#111827' };

export const DARK_BG: Record<string, string> = {
  default: DARK.page, white: '#05070a', cool: '#0e1521', warm: '#15120e', mint: '#0d1713', lavender: '#13101f',
};

/** Vygeneruje pravidla pro třídu a (volitelně) její hover variantu. */
function cls(out: string[], name: string, decl: string, hover = true, disabled = false) {
  out.push(`.${esc(name)}{${decl}}`);
  if (hover) out.push(`.${esc('hover:' + name)}:hover{${decl}}`);
  if (disabled) out.push(`.${esc('disabled:' + name)}:disabled{${decl}}`);
}

function neutrals(): string {
  const o: string[] = [];
  for (const [k, v] of Object.entries(GRAY_BG)) {
    const n = k === 'white' ? 'bg-white' : `bg-gray-${k}`;
    cls(o, n, `background-color:${v} !important`, true, true);
  }
  // slate se používá jen výjimečně – chová se jako šedá
  cls(o, 'bg-slate-50', `background-color:${DARK.raised} !important`);
  cls(o, 'bg-slate-100', `background-color:#232b37 !important`);
  for (const [k, v] of Object.entries(GRAY_TEXT)) cls(o, `text-gray-${k}`, `color:${v} !important`, true, true);
  cls(o, 'text-slate-700', `color:${GRAY_TEXT['700']} !important`);
  for (const [k, v] of Object.entries(GRAY_BORDER)) {
    cls(o, `border-gray-${k}`, `border-color:${v} !important`);
    o.push(`.divide-gray-${k}>:not([hidden])~:not([hidden]){border-color:${v} !important}`);
  }
  cls(o, 'border-slate-200', `border-color:${GRAY_BORDER['200']} !important`);
  // poloprůhledné varianty
  o.push(`.bg-gray-50\\/80,.bg-gray-50\\/60{background-color:${rgba(DARK.raised, 0.8)} !important}`);
  o.push(`.bg-gray-100\\/70,.bg-gray-200\\/60{background-color:${rgba('#2d3644', 0.7)} !important}`);

  // barevné rodiny (štítky, upozornění…)
  for (const [f, [c3, c4, c5]] of Object.entries(FAM)) {
    for (const [s, a] of [['50', 0.1], ['100', 0.16], ['200', 0.24]] as const) cls(o, `bg-${f}-${s}`, `background-color:${rgba(c5, a)} !important`);
    for (const s of ['500', '600', '700']) cls(o, `text-${f}-${s}`, `color:${c4} !important`);
    for (const s of ['800', '900']) cls(o, `text-${f}-${s}`, `color:${c3} !important`);
    for (const [s, a] of [['100', 0.2], ['200', 0.3], ['300', 0.4]] as const) cls(o, `border-${f}-${s}`, `border-color:${rgba(c5, a)} !important`);
  }
  return o.join('\n');
}

function accentRules(t: AppTheme): string {
  const A = t.accent;
  const txt = tint(A, 0.32), txtHover = tint(A, 0.5);
  const o: string[] = [];
  for (const [s, a] of [['50', 0.12], ['100', 0.2], ['200', 0.28]] as const) cls(o, `bg-blue-${s}`, `background-color:${rgba(A, a)} !important`);
  for (const s of ['400', '500', '600', '700', '800', '900']) cls(o, `text-blue-${s}`, `color:${s === '400' ? txtHover : txt} !important`);
  for (const [s, a] of [['100', 0.25], ['200', 0.35], ['300', 0.45], ['400', 0.55]] as const) cls(o, `border-blue-${s}`, `border-color:${rgba(A, a)} !important`);
  o.push(`.group:hover .group-hover\\:text-blue-400{color:${txtHover} !important}`);
  return o.join('\n');
}

export function buildDarkCSS(t: AppTheme, cfg: UiConfig = {}): string {
  const A = t.accent;
  const bg = cfg.bg ?? 'default';
  const pageBg = bg === 'tint' ? mix(A, DARK.page, 0.88)
    : bg === 'gradient' ? `linear-gradient(160deg, ${mix(A, DARK.page, 0.84)} 0%, ${DARK.page} 55%, ${mix(A, DARK.page, 0.92)} 100%)`
    : DARK_BG[bg] ?? DARK.page;

  const p: string[] = [];
  p.push(':root{color-scheme:dark}');
  p.push(neutrals());
  p.push(accentRules(t));

  // postranní panel (po neutrálech, aby přebil .text-gray-300 apod.)
  p.push(`
.app-sidebar{background-color:${t.sidebar} !important;color:#fff !important;border-right:1px solid ${t.sidebarBorder}}
.app-sidebar-border{border-color:${t.sidebarBorder} !important}
.app-sidebar-text{color:${t.sidebarText} !important}
.app-sidebar-hover:hover{background-color:${t.sidebarHover} !important;color:#fff !important}
.app-sidebar-active{background-color:${t.sidebarActive} !important;color:#fff !important}
.app-sidebar-logo{background-color:${A} !important}
.settings-nav-active{background-color:${A} !important;color:#fff !important}`);

  p.push(`.app-bg{background:${pageBg} !important}`);
  if (cfg.topbar === 'tint') p.push(`.app-topbar{background-color:${mix(A, DARK.card, 0.86)} !important;border-color:${mix(A, DARK.card, 0.7)} !important}`);
  if (cfg.topbar === 'clear') p.push(`.app-topbar{background-color:transparent !important;border-color:transparent !important}`);
  if (cfg.buttonStyle === 'soft') {
    p.push(`button.bg-blue-600:not(.app-sidebar-active),a.bg-blue-600:not(.app-sidebar-active){background-color:${mix(A, DARK.card, 0.78)} !important;color:${tint(A, 0.4)} !important}
button.bg-blue-600:not(.app-sidebar-active):hover,a.bg-blue-600:not(.app-sidebar-active):hover{background-color:${mix(A, DARK.card, 0.68)} !important}`);
  }
  if (cfg.cards === 'shadow') {
    p.push(`div[class*="bg-white"][class*="border"][class*="rounded-"]{box-shadow:0 1px 3px rgba(0,0,0,.4),0 10px 28px -10px rgba(0,0,0,.6) !important;border-color:rgba(255,255,255,.06) !important}`);
  }

  // formuláře, grafy, text obchodních podmínek, drobnosti
  p.push(`
:where(input:not([type=checkbox]):not([type=radio]):not([type=range]):not([type=color]):not([type=file]),select,textarea){background-color:${DARK.input}}
input.bg-white,select.bg-white,textarea.bg-white{background-color:${DARK.input} !important}
input::placeholder,textarea::placeholder{color:#6b7686}
iframe{background-color:#fff !important;color-scheme:light}
.absolute.rounded-full.bg-white{background-color:#f1f5f9 !important}
.bg-amber-400.text-amber-900{color:#451a03 !important}
.terms-body h2,.terms-body h3,.terms-body strong{color:#f1f5f9 !important}
.terms-body h4{color:#d3d9e2 !important}
.recharts-cartesian-grid line{stroke:#2a3340}
.recharts-default-tooltip,.recharts-tooltip-wrapper .recharts-default-tooltip{background-color:${DARK.raised} !important;border-color:#2a3340 !important}
.recharts-tooltip-label,.recharts-tooltip-item{color:#e6eaf0 !important}`);

  // „papír“ (návrhář a náhled faktury) zůstává světlý
  const k: string[] = [`.keep-light{background-color:#fff !important;color:#111827 !important;color-scheme:light}`];
  for (const [s, v] of Object.entries(L_GRAY_TEXT)) k.push(`.keep-light .text-gray-${s}{color:${v} !important}`);
  for (const [s, v] of Object.entries(L_GRAY_BG)) k.push(`.keep-light .bg-gray-${s}{background-color:${v} !important}`);
  k.push(`.keep-light .bg-white{background-color:#fff !important}`);
  for (const [s, v] of Object.entries(L_GRAY_BORDER)) k.push(`.keep-light .border-gray-${s}{border-color:${v} !important}`);
  for (const [f, c] of Object.entries(L_FAM)) {
    k.push(`.keep-light .text-${f}-600{color:${c.t6} !important}.keep-light .text-${f}-700{color:${c.t7} !important}`);
    k.push(`.keep-light .bg-${f}-50{background-color:${c.b50} !important}.keep-light .bg-${f}-100{background-color:${c.b100} !important}`);
    k.push(`.keep-light .border-${f}-200{border-color:${c.bd200} !important}`);
  }
  k.push(`.keep-light .bg-blue-50,.keep-light .bg-blue-100{background-color:${t.accentLight} !important}`);
  k.push(`.keep-light .text-blue-600{color:${t.accent} !important}.keep-light .text-blue-700{color:${t.accentText} !important}`);
  p.push(k.join('\n'));

  return `@media screen{\n${p.join('\n')}\n}`;
}
